import type { FunnelMetricsResponse } from "@voice2mail/shared/v2m_api";

import { MetricCard } from "./MetricCard";

interface AcquisitionFunnelProps {
  data: FunnelMetricsResponse;
}

function formatRate(rate: number, hasDenominator: boolean): string {
  return hasDenominator ? `${(rate * 100).toFixed(1)}%` : "brak danych";
}

/**
 * V2M-DEV-163: `GET /admin/metrics/funnel` (E4/E5) existed with zero panel
 * consumers. E4 ("instalacja -> pierwsza generacja") and E5 ("pierwsza
 * generacja -> płatność") are computed live, all-time, straight off the
 * primary `users`/`generations`/`revenue_ledger` collections — not a
 * downstream aggregate that can silently stop being written — so unlike
 * unit-economics, this endpoint needs no NEW backend field to distinguish "a
 * real 0% rate" from "no data to compute a rate from": the payload already
 * carries both the rate AND its own denominator (`registeredUsers` for E4,
 * E4's `activatedUsers` for E5 — see backend `FunnelStageE5` docstring for
 * why E5 divides by activation, not registration). A rate is only rendered
 * as a percentage when its denominator is non-zero; otherwise "brak danych".
 *
 * `e5.measurable` is a separate, explicit backend flag (defaults `true`
 * today, but part of the wire contract) for the case where E5 cannot be
 * computed at all yet — rendered as its own banner, never silently coerced
 * into a 0%/N/A rate.
 */
export function AcquisitionFunnel({ data }: AcquisitionFunnelProps) {
  const { e4, e5, e4Breakdown } = data;
  const e4RateDisplay = formatRate(e4.activationRate, e4.registeredUsers > 0);
  const e5RateDisplay = formatRate(e5.conversionRate, e4.activatedUsers > 0);

  return (
    <div className="space-y-6">
      <div className="space-y-2" data-testid="funnel-stage-e4">
        <h4 className="text-sm font-semibold text-slate-700">
          E4 — instalacja → pierwsza generacja
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
          <MetricCard label="Zarejestrowani" value={e4.registeredUsers} />
          <MetricCard label="Aktywowani" value={e4.activatedUsers} />
          <MetricCard
            label="Wskaźnik aktywacji"
            value={e4RateDisplay}
            warn={e4.registeredUsers === 0}
          />
        </div>
      </div>

      {/* V2M-DEV-196: `e4` above measures install -> first-generation as ONE
          jump. `e4Breakdown` (additive backend field, `ActivationFunnelE4Breakdown`,
          commit 290f94d1) reads the `activation_funnel_milestones` per-user
          mirror for the three intermediate steps Plausible cannot supply a
          per-person funnel for (the fourth step, first generation, comes from
          `generations`) — see that model's own docstring, backend.
          Optional/nullable: rendered only when present, so an old cached
          payload (or a backend that has not deployed this field yet) shows
          the same E4/E5 it always did, not a broken section. */}
      {e4Breakdown != null && (
        <div className="space-y-2" data-testid="funnel-stage-e4-breakdown">
          <h4 className="text-sm font-semibold text-slate-700">
            E4 — kroki pośrednie (instalacja → pierwsza generacja)
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:max-w-3xl">
            <MetricCard
              label="Zgoda na mikrofon"
              value={e4Breakdown.micPermissionGranted}
              hint="mic_permission_granted"
            />
            <MetricCard
              label="Pierwsze otwarcie panelu"
              value={e4Breakdown.firstPanelOpened}
              hint="first_panel_opened"
            />
            <MetricCard
              label="Pierwsze nagranie"
              value={e4Breakdown.firstRecordingStarted}
              hint="first_recording_started"
            />
            <MetricCard
              label="Pierwsza generacja"
              value={e4Breakdown.firstAiGenerationCompleted}
              hint="first_ai_generation_completed"
            />
          </div>
          {/* Not-implemented-error-over-silent-fake territory: a lower-bound
              number rendered like a real count would be read as an
              activation-rate crisis that may not exist. `isLowerBound` is
              `true` on every payload today (see the backend model's own
              docstring — three of the steps fire before an account
              exists on a genuinely anonymous session, and this mirror is
              written ONLY from the authenticated endpoint) — visible text,
              not just a `hint` tooltip nobody hovers over on a metrics
              dashboard. */}
          {e4Breakdown.isLowerBound !== false && (
            <p className="text-xs text-slate-500">
              Dolne ograniczenie (lower bound) — realna liczba może być wyższa. Dwie przyczyny:
              kroki poprzedzające rejestrację mogły zajść, zanim istniało konto do ich zapisania, a
              kroki 1–3 są zapisywane tylko przy udzielonej zgodzie na telemetrię wtyczki (brak
              decyzji lub wygaśnięcie zgody ukrywa je). Krok „Pierwsza generacja” liczony jest z
              wykonanych generacji i zgody nie wymaga.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2" data-testid="funnel-stage-e5">
        <h4 className="text-sm font-semibold text-slate-700">E5 — pierwsza generacja → płatność</h4>
        {e5.measurable === false ? (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" role="alert">
            <div className="text-amber-800 text-sm font-medium">E5 jeszcze niemierzalne.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
            <MetricCard label="Płacący" value={e5.paidUsers} />
            <MetricCard
              label="Konwersja (od aktywowanych)"
              value={e5RateDisplay}
              warn={e4.activatedUsers === 0}
            />
          </div>
        )}
      </div>
    </div>
  );
}
